import type * as React from 'react'

import { cn } from '../../lib/utils'
import { RULED_HEAD, SATR } from './section'

/**
 * A ruled section that owns its rows (DESIGN.md "Ruled sections", "The
 * Ruled-not-Boxed Rule"). It is not a box: no border, no corner, no shadow,
 * and the page's own paper behind it.
 *
 * The section bleeds 20px past its text on each side (16px below 900px): the
 * panel takes a negative inline margin of `--bleed` and puts the same amount
 * back as padding in its head, body and footer, so its text starts on the
 * page title's edge while its rules overhang the text block, as on ruled
 * paper. The page column's 32px inset leaves room for the overhang.
 *
 * Side-by-side panels need a 64px gutter (`gap-x-gutter`), where the rules
 * break; a narrower gap lets the two overhangs meet.
 *
 * `--table-edge` tells a `Table` inside how far its first and last cells pad
 * themselves, so a flush table's text aligns to the same edge. `PanelBody`
 * resets it, because its own inset already does that job.
 */
function Panel({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel"
      className={cn(
        'animate-enter text-card-foreground -mx-bleed flex min-w-0 flex-col [--table-edge:var(--bleed)]',
        className,
      )}
      {...props}
    />
  )
}

/**
 * The head: the title on its satr over the hairline, meta and controls 10px
 * above the rule at the end. A trailing `PanelAction` pushes itself to the
 * end side.
 */
function PanelHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel-header"
      className={cn(
        RULED_HEAD,
        'shrink-0 gap-y-2 px-bleed',
        '[&>[data-slot=panel-action]]:ms-auto',
        className,
      )}
      {...props}
    />
  )
}

/**
 * The section's mark: a lucide icon in a square ruled tile, the head's first
 * child. It names the kind of content at a glance and fills the start column
 * when the section sits in margin-heading mode. Decoration only, so it is
 * hidden from assistive technology; the title carries the meaning.
 */
function PanelIcon({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="panel-icon"
      aria-hidden="true"
      className={cn(
        'border-border text-muted-foreground flex size-8 shrink-0 items-center justify-center border',
        "[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  )
}

/** Alexandria 600 at 16px, on its satr. */
function PanelTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel-title"
      data-satr=""
      className={cn(SATR, 'text-section-title text-foreground min-w-0', className)}
      {...props}
    />
  )
}

/** Head meta: "P08 · reporting currency USD", "owner-only items". */
function PanelMeta({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel-meta"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

function PanelAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel-action"
      className={cn('flex flex-wrap items-center gap-2', className)}
      {...props}
    />
  )
}

/**
 * Flush: a table or a list spans the whole section, overhang included, and
 * pads its own first and last cells by `--table-edge`. Prose, fact grids and
 * forms use `PanelBody`.
 */
function PanelContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="panel-content" className={cn('min-h-0 flex-1', className)} {...props} />
}

/**
 * The standard inset for prose, fact grids and form content: the bleed on
 * each side, so the text aligns to the page title's edge, and 16px above and
 * below.
 */
function PanelBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel-body"
      className={cn('min-h-0 flex-1 px-bleed py-4 [--table-edge:0.75rem]', className)}
      {...props}
    />
  )
}

/** The foot rule: "Open audit log", a count, a link. `mt-auto` keeps it on the floor. */
function PanelFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel-footer"
      className={cn(
        'border-border text-muted-foreground mt-auto flex min-h-10 shrink-0 flex-wrap items-center justify-between gap-3 border-t px-bleed py-2.5 text-sm',
        className,
      )}
      {...props}
    />
  )
}

export {
  Panel,
  PanelAction,
  PanelBody,
  PanelContent,
  PanelFooter,
  PanelHeader,
  PanelIcon,
  PanelMeta,
  PanelTitle,
}
